import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { BellOff } from 'lucide-react';
import { getPushPermissionState, requestNotificationPermission, getPushPlatform } from '../services/pushService';
import { openSystemSettings } from './NotificationReengagementBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Always-visible red bar on Home while the phone/browser notification permission is off.
export const NotificationsOffBar = () => {
  const [state, setState] = useState(null);
  const tappedRef = useRef(false);
  const refresh = useCallback(() => {
    getPushPermissionState().then((s) => {
      // Activation counted only when it follows a tap on this bar (prompt or via phone settings).
      if (s === 'granted' && tappedRef.current) {
        tappedRef.current = false;
        axios.post(`${API}/notifications/off-bar-activated`).catch(() => {});
      }
      setState(s);
    }).catch(() => setState('unsupported'));
  }, []);

  useEffect(() => {
    refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    let sub = null;
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App }) => App.addListener('appStateChange', ({ isActive }) => { if (isActive) refresh(); }))
        .then((h) => { sub = h; }).catch(() => {});
    }
    return () => { document.removeEventListener('visibilitychange', onVisible); sub?.remove?.(); };
  }, [refresh]);

  if (state !== 'prompt' && state !== 'denied') return null;

  const activate = async () => {
    tappedRef.current = true;
    if (state === 'denied') {
      if (Capacitor.isNativePlatform()) {
        toast("Attiva l'interruttore Notifiche", { duration: 7000 });
        await openSystemSettings();
      } else {
        toast("Tocca il lucchetto accanto all'indirizzo del sito, poi Notifiche e Consenti", { duration: 8000 });
      }
      return;
    }
    let token = null;
    try { token = await requestNotificationPermission(); } catch { token = null; }
    if (token) await axios.post(`${API}/notifications/register-token`, { token, platform: getPushPlatform() }).catch(() => {});
    refresh();
  };

  return (
    <div className="mb-3 rounded-2xl bg-red-600 border border-red-400 shadow-[0_0_18px_rgba(239,68,68,0.55)] p-3 flex items-center gap-3" data-testid="notifications-off-bar">
      <BellOff className="w-5 h-5 text-white shrink-0" />
      <p className="flex-1 text-xs sm:text-sm font-bold text-white leading-snug" data-testid="notifications-off-bar-text">
        Hai le notifiche disattivate: come facciamo ad avvisarti di gol e nuove analisi?
      </p>
      <button onClick={activate} className="shrink-0 px-3 py-2 rounded-xl bg-white text-red-600 text-xs font-black active:scale-95 transition-transform" data-testid="notifications-off-bar-btn">
        Attiva notifiche
      </button>
    </div>
  );
};

export default NotificationsOffBar;
